import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, RefreshCw, Undo2, Archive, RotateCcw } from "lucide-react";
import { api, money, shopDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { KuberInvoiceLookup } from "@/components/KuberInvoiceLookup";

const TABS = [
  { key: "unmatched", label: "Waiting to be matched" },
  { key: "matched", label: "Matched" },
  { key: "all", label: "All successful payments" },
  { key: "failed", label: "Failed payments" },
  { key: "dismissed", label: "Put aside" },
];

// PayID is the one method where the shop absorbs Kuber's fee, so the invoice it clears
// is Amount + Fees. Keep this in step with kuber_is_payid() in the backend.
const isPayId = (m) => {
  const t = String(m || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return t === "payid" || t === "pay id" || t === "pay2" || t.replace(/ /g, "") === "payid";
};

export default function KuberPayments() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("unmatched");
  const [busy, setBusy] = useState("");
  const { user } = useAuth();
  const canPutAside = ["admin", "it_admin"].includes(user?.role);

  const load = useCallback(() => {
    api
      .get("/kuber/payments", { params: { status: tab } })
      .then(({ data }) => setData(data))
      .catch(() => setData({ rows: [], pending: 0, failed: 0 }));
  }, [tab]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const match = async (eventId, docId) => {
    setBusy(eventId);
    try {
      const { data: res } = await api.post(`/kuber/payments/${eventId}/match`, {
        doc_id: docId,
      });
      toast.success(
        res.relabelled_from
          ? `Invoice #${res.doc_number}: ${res.relabelled_from} relabelled as ${res.method}`
          : `Applied to invoice #${res.doc_number}` +
              (res.paid_in_full ? " — paid in full" : ` — ${money(res.balance)} still owing`)
      );
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not apply that payment"
      );
    } finally {
      setBusy("");
    }
  };

  const recordRefund = async (row) => {
    const entered = window.prompt(
      `Kuber refunds are arranged with Kuber support, not from here.\n\n` +
        `Once Kuber confirm the refund, enter the amount they sent back (max ${money(
          row.amount || 0
        )}) and it will come off invoice #${row.matched_doc_number}.`,
      String(row.amount ?? "")
    );
    if (entered === null) return;
    const amount = Number(String(entered).replace(/[^0-9.]/g, ""));
    if (!amount) return toast.error("Enter the refunded amount");
    const note = window.prompt("Reference from Kuber support (optional)", "") || "";
    setBusy(row.id);
    try {
      const { data: res } = await api.post(`/kuber/payments/${row.id}/refund`, {
        amount,
        note,
      });
      toast.success(
        `Refund recorded — invoice #${res.doc_number} now shows ${money(res.balance)} owing`
      );
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not record that refund"
      );
    } finally {
      setBusy("");
    }
  };

  const putAside = async (row) => {
    const warning =
      `PUT THIS PAYMENT ASIDE?\n\n` +
      `${money(row.amount ?? 0)}${row.transaction_id ? ` · transaction ${row.transaction_id}` : ""}\n\n` +
      `It will stop appearing as waiting to be matched, and will no longer be counted ` +
      `in the Kuber badge.\n\n` +
      `Only do this if it is NOT real money owed to the shop — a test push from Kuber, ` +
      `or a payment you have already taken at the counter.\n\n` +
      `If this is a genuine customer payment, close this and match it to the invoice ` +
      `instead. Putting a real payment aside leaves the invoice showing as unpaid.\n\n` +
      `Nothing is deleted and you can bring it back. Continue?`;
    if (!window.confirm(warning)) return;
    const reason = window.prompt(
      "Why are you putting it aside? This is recorded against your name.",
      "Kuber test push"
    );
    if (reason === null) return;
    if (reason.trim().length < 3) return toast.error("Give a short reason");
    setBusy(row.id);
    try {
      await api.post(`/kuber/payments/${row.id}/dismiss`, { reason: reason.trim() });
      toast.success("Put aside — it is in the Put aside tab if you need it");
      load();
    } catch (err) {
      toast.error(
        (err.response?.data?.detail && String(err.response.data.detail)) ||
          "Could not put that payment aside"
      );
    } finally {
      setBusy("");
    }
  };

  const restore = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/kuber/payments/${row.id}/restore`);
      toast.success("Back on the matching list");
      load();
    } catch (err) {
      toast.error("Could not bring that payment back");
    } finally {
      setBusy("");
    }
  };

  const rows = data?.rows || [];

  return (
    <div data-testid="kuber-payments-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#B38F4E]">Kuber</p>
          <h1 className="text-3xl font-bold">Kuber payments</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Payments Kuber has told us about. When Kuber send our invoice number back the
            payment goes straight onto that invoice; otherwise a payment that clearly
            settles one open invoice is applied on its own, and anything else waits here
            for you to tap the right invoice. For PayID the shop absorbs Kuber's fee — the
            invoice clears for the full amount and the fee is logged as a cost; every other
            method charges Kuber's fee to the customer. Either way the printed invoice is
            unchanged.
          </p>
        </div>
        <Button
          variant="outline"
          className="rounded-none"
          onClick={load}
          data-testid="kuber-refresh"
        >
          <RefreshCw className="mr-1.5 h-4 w-4" /> Refresh
        </Button>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`kuber-tab-${t.key}`}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
              tab === t.key
                ? "border-[#C5A059] text-[#0A0A0A]"
                : "border-transparent text-muted-foreground hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
            {t.key === "unmatched" && data?.pending ? ` (${data.pending})` : ""}
            {t.key === "matched" && data?.matched ? ` (${data.matched})` : ""}
            {t.key === "failed" && data?.failed ? ` (${data.failed})` : ""}
            {t.key === "dismissed" && data?.put_aside ? ` (${data.put_aside})` : ""}
          </button>
        ))}
      </div>

      {tab === "failed" && (
        <p
          className="mt-4 border border-[#9A3412] bg-[#FEF6F2] p-3 text-sm text-[#9A3412]"
          data-testid="kuber-failed-note"
        >
          <AlertTriangle className="mr-1.5 inline h-4 w-4" />
          Anything Kuber does not call a success is an unpaid invoice. Do not hand over
          goods until the customer has settled it in full.
        </p>
      )}

      {tab === "dismissed" && (
        <p
          className="mt-4 border border-border bg-[#FBF8F2] p-3 text-sm text-muted-foreground"
          data-testid="kuber-dismissed-note"
        >
          <Archive className="mr-1.5 inline h-4 w-4" />
          Payments put aside. Nothing has been deleted — if one of these turns out to be a
          real customer payment, bring it back and match it to the invoice.
        </p>
      )}

      {rows.length === 0 && (
        <div
          className="mt-6 border border-border bg-[#FBF8F2] p-10 text-center text-muted-foreground"
          data-testid="kuber-empty"
        >
          {tab === "failed"
            ? "No failed Kuber payments."
            : tab === "dismissed"
              ? "Nothing has been put aside."
              : tab === "matched"
                ? "No Kuber payments have been matched to an invoice yet."
                : tab === "all"
                  ? "No Kuber payments recorded yet."
                  : "Nothing waiting — every Kuber payment has been matched to an invoice."}
        </div>
      )}

      <div className="mt-6 space-y-4">
        {rows.map((r) => (
          <div
            key={r.id}
            className="border border-border bg-white p-4"
            data-testid={`kuber-row-${r.id}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <span className="tabular text-2xl font-bold">{money(r.amount ?? 0)}</span>
                <span className="ml-3 text-xs uppercase tracking-wider text-muted-foreground">
                  {shopDateTime(r.received_at) || r.date} {r.method && `· ${r.method}`}
                </span>
                {r.customer_name && <span className="ml-3 text-sm">{r.customer_name}</span>}
              </div>
              {r.outcome !== "success" ? (
                <span
                  className="border border-[#9A3412] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#9A3412]"
                  data-testid={`kuber-failed-${r.id}`}
                >
                  Failed {r.status_raw ? `· ${r.status_raw}` : ""}
                </span>
              ) : r.dismissed ? (
                <span
                  className="border border-border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  data-testid={`kuber-put-aside-${r.id}`}
                >
                  Put aside
                </span>
              ) : r.matched_doc_id ? (
                <span
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#166534]"
                  data-testid={`kuber-matched-${r.id}`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <Link
                    to={`/documents/${r.matched_doc_id}`}
                    className="underline hover:text-[#0A0A0A]"
                  >
                    Invoice #{r.matched_doc_number}
                  </Link>
                  {r.matched_on === "invoice_number"
                    ? " (matched on invoice number)"
                    : r.auto_matched
                      ? " (matched automatically)"
                      : ""}
                </span>
              ) : (
                <span
                  className="border border-[#9A3412] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#9A3412]"
                  data-testid={`kuber-unmatched-${r.id}`}
                >
                  Not matched
                </span>
              )}
            </div>

            <p className="tabular mt-1 text-xs text-muted-foreground">
              {r.reference ? `Our invoice ${r.reference} · ` : ""}
              Kuber order {r.order_id || "—"}
              {r.transaction_id && ` · transaction ${r.transaction_id}`}
              {r.fee
                ? ` · Kuber fee ${money(r.fee)} ${
                    isPayId(r.method)
                      ? `(absorbed by the shop — settles a ${money(
                          (r.amount || 0) + (r.fee || 0)
                        )} invoice)`
                      : "(paid by the customer to Kuber)"
                  }`
                : ""}
              {r.failure_reason && ` · ${r.failure_reason}`}
            </p>

            {r.dismissed && (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-xs text-muted-foreground" data-testid={`kuber-aside-why-${r.id}`}>
                  Put aside by {r.dismissed_by || "—"}
                  {r.dismissed_reason ? ` — ${r.dismissed_reason}` : ""}
                </p>
                {canPutAside && (
                  <Button
                    variant="outline"
                    className="h-8 rounded-none text-xs"
                    disabled={busy === r.id}
                    onClick={() => restore(r)}
                    data-testid={`kuber-restore-${r.id}`}
                  >
                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Bring it back
                  </Button>
                )}
              </div>
            )}

            {r.outcome === "success" && r.matched_doc_id && (
              <div className="mt-3">
                <Button
                  variant="outline"
                  className="h-8 rounded-none text-xs"
                  disabled={busy === r.id}
                  onClick={() => recordRefund(r)}
                  data-testid={`kuber-refund-${r.id}`}
                >
                  <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Record a Kuber refund
                </Button>
              </div>
            )}

            {r.outcome === "success" && !r.matched_doc_id && !r.dismissed && (
              <div className="mt-3 border-t border-border pt-3">
                {canPutAside && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      variant="outline"
                      className="h-8 rounded-none text-xs"
                      disabled={busy === r.id}
                      onClick={() => putAside(r)}
                      data-testid={`kuber-dismiss-${r.id}`}
                    >
                      <Archive className="mr-1.5 h-3.5 w-3.5" /> Not ours — put it aside
                    </Button>
                  </div>
                )}
                {r.candidates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No open Kuber invoice matches this amount from the last{" "}
                    {data?.window_hours ?? 48} hours. If the invoice was already marked
                    paid as Card or Cash, find it by number below and Match will relabel it.
                  </p>
                ) : (
                  <>
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                      Which invoice is this for?
                    </p>
                    <div className="mt-2 flex flex-col gap-2">
                      {r.candidates.map((c) => (
                        <div
                          key={c.id}
                          className="flex flex-wrap items-center justify-between gap-3 border border-border bg-[#FBF8F2] px-3 py-2"
                        >
                          <span className="text-sm">
                            <span className="tabular font-semibold">#{c.doc_number}</span> ·{" "}
                            {c.customer_name || "No name"} · {c.branch} · {c.date} ·{" "}
                            <span className="tabular">{money(c.balance)} owing</span>
                            {c.partial && (
                              <span className="ml-2 text-[11px] font-semibold uppercase tracking-wider text-[#9A3412]">
                                part payment
                              </span>
                            )}
                          </span>
                          <Button
                            className="h-9 rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
                            disabled={busy === r.id}
                            onClick={() => match(r.id, c.id)}
                            data-testid={`kuber-match-${r.id}-${c.doc_number}`}
                          >
                            {c.partial ? "Apply as part payment" : "Apply to this invoice"}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                <KuberInvoiceLookup row={r} busy={busy === r.id} onMatch={match} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
